import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { Plus, Trash2, Save, X, Building2 } from "lucide-react";

const empty = {
  name: "", tag: "", city: "", description: "", story: "", impact: "",
  image_url: "", logo_url: "", website: "", youth_reached: "", published: false,
};

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">{label}</div>
    {children}
  </div>
);
const Input = (p) => <input {...p} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />;
const Textarea = (p) => <textarea rows={p.rows || 3} {...p} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />;

const OrgForm = ({ initial, onSubmit, onCancel, saving }) => {
  const [f, setF] = useState(initial || empty);
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF(s => ({ ...s, [k]: v }));
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="rounded-lg p-6 bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="org-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[18px] font-medium">{initial?.id ? "Edit organisation" : "New organisation"}</h3>
        <button type="button" onClick={onCancel} className="text-[13px] link-underline inline-flex items-center gap-1"><X size={14} /> Cancel</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Name"><Input required data-testid="org-name" value={f.name} onChange={set('name')} /></Field>
        <Field label="Tag"><Input value={f.tag} onChange={set('tag')} placeholder="STEM, Entrepreneurship…" /></Field>
        <Field label="City"><Input value={f.city} onChange={set('city')} /></Field>
        <Field label="Website"><Input type="url" value={f.website} onChange={set('website')} placeholder="https://…" /></Field>
        <Field label="Youth reached"><Input value={f.youth_reached} onChange={set('youth_reached')} placeholder="e.g. 1,200+" /></Field>
        <Field label="Logo URL"><Input value={f.logo_url} onChange={set('logo_url')} /></Field>
        <Field label="Hero image URL" full><Input value={f.image_url} onChange={set('image_url')} /></Field>
        <Field label="Short description" full><Textarea rows={2} value={f.description} onChange={set('description')} /></Field>
        <Field label="Story" full><Textarea rows={4} value={f.story} onChange={set('story')} /></Field>
        <Field label="Impact" full><Textarea rows={3} value={f.impact} onChange={set('impact')} /></Field>
        <Field label="Published">
          <label className="inline-flex items-center gap-2 text-[13px] mt-2"><input type="checkbox" data-testid="org-published" checked={!!f.published} onChange={set('published')} /> Show on public site</label>
        </Field>
      </div>
      <div className="mt-6 flex items-center gap-2">
        <button type="submit" disabled={saving} data-testid="org-save-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
      </div>
    </form>
  );
};

const CMSOrganisations = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi.get('/admin/organisations'); setList(data); }
    catch { toast({ title: "Failed to load organisations" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    setSaving(true);
    try { await adminApi.post('/admin/organisations', payload); toast({ title: "Organisation created" }); setMode('list'); load(); }
    catch (err) { toast({ title: "Create failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };
  const update = async (payload) => {
    setSaving(true);
    try { await adminApi.patch(`/admin/organisations/${editing.id}`, payload); toast({ title: "Updated" }); setMode('list'); setEditing(null); load(); }
    catch (err) { toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}"?`)) return;
    try { await adminApi.delete(`/admin/organisations/${o.id}`); toast({ title: "Deleted" }); load(); }
    catch (err) { toast({ title: "Delete failed", description: err?.response?.data?.detail || "Try again." }); }
  };

  if (mode === 'new') return <OrgForm onSubmit={create} onCancel={() => setMode('list')} saving={saving} />;
  if (mode === 'edit') return <OrgForm initial={editing} onSubmit={update} onCancel={() => { setMode('list'); setEditing(null); }} saving={saving} />;

  return (
    <div className="rounded-lg p-6" style={{ background: '#fbf8ef' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[20px] font-medium">Organisations</h3>
        <button onClick={() => setMode('new')} data-testid="org-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]"><Plus size={14} /> New organisation</button>
      </div>
      {loading ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">Loading…</div>
        : list.length === 0 ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">No organisations yet.</div>
        : (
          <div className="overflow-x-auto bg-white rounded-md">
            <table className="w-full">
              <thead className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
                <tr>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Tag</th>
                  <th className="text-left py-2 px-3">City</th>
                  <th className="text-left py-2 px-3">Published</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(o => (
                  <tr key={o.id} className="border-b last:border-b-0 hover:bg-[#f6f0dc]/60" style={{ borderColor: 'rgba(13,42,32,0.06)' }}>
                    <td className="py-2 px-3 text-[13px] font-medium"><Building2 size={12} className="inline mr-1 text-[#0d2a20]/50" />{o.name}</td>
                    <td className="py-2 px-3 text-[12px] text-[#0d2a20]/70">{o.tag || '—'}</td>
                    <td className="py-2 px-3 text-[12px] text-[#0d2a20]/70">{o.city || '—'}</td>
                    <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background: o.published ? '#d6ecd6' : '#ece5d3' }}>{o.published ? 'Published' : 'Draft'}</span></td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditing(o); setMode('edit'); }} data-testid={`org-edit-${o.id}`} className="px-2 py-1 rounded btn-ghost text-[11px]">Edit</button>
                        <button onClick={() => remove(o)} className="p-1.5 rounded hover:bg-[#f3d6d1] text-[#7a1f13]" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
};

export default CMSOrganisations;
